import { useEffect, useState } from 'react';
import ProjectDocumentsStatusAPI from '../services/api/ai/project-docs/project-documents-status-api';
import Logger from '../utils/logger';

/**
 * Whether to show Project Documents (#250) to the signed-in user.
 *
 * `enabled` is true only after GET /api/ai/project-docs/status answered enabled: true
 * for THIS super admin and THIS session (user id + token). The stored answer
 * is tagged with that session, so after logout, an account change or a new
 * token it no longer counts: the result is false (hidden) until the new
 * session's status arrives. Not a super admin, still loading, or a failed
 * request -> false. No polling: one request per session.
 *
 * Returns { enabled, pending }: `pending` is true only while a super admin's
 * request for this session has not answered yet, so the Assistant's address
 * can show nothing for that moment instead of the not-found page.
 */
export default function useProjectDocumentsStatus({
  token,
  userId,
  isSuperAdmin,
}) {
  const session = token && isSuperAdmin ? `${userId}|${token}` : null;
  const [status, setStatus] = useState({ session: null, enabled: false });

  useEffect(() => {
    if (!session) return undefined;
    let cancelled = false;
    ProjectDocumentsStatusAPI.isEnabled(token)
      .then((enabled) => {
        if (!cancelled) setStatus({ session, enabled });
      })
      .catch((err) => {
        Logger.error('Could not load Project Documents status', err);
        if (!cancelled) setStatus({ session, enabled: false });
      });
    return () => {
      cancelled = true;
    };
  }, [session, token]);

  const answered = session !== null && status.session === session;
  return {
    enabled: answered && status.enabled,
    pending: session !== null && !answered,
  };
}
